import { PagedItems } from '@texas/types';
import { httpClient } from '../httpClient/httpClient';
import { UserType } from './userApi';
import { SimpleFileLink } from './filesApi';
import {
  ComplianceDocumentKind,
  ComplianceDocumentType,
} from './metadata/complianceDocumentDefinitionsApi';

export interface CustomerComplianceDocument {
  complianceDocumentDefinitionId: number;
  name: string;
  description: string;
  kind: ComplianceDocumentKind;
  type: ComplianceDocumentType;
  archived: string | null;
  logo: SimpleFileLink | null;
  added: string;
  branches: CustomerComplianceDocumentBranch[];
}

export interface CustomerComplianceDocumentBranch {
  id: number;
  name: string;
  identifier: string;
}

export interface CustomerComplianceDocumentBranchesRequest {
  branchIds: number[];
}

export interface BrandsDto {
  name: string;
  id: number;
  erpId: string;
  nrOfArticles: number;
  project: string;
  logoIdentifier: string | null;
  currentUserRoles: string[];
  currentUserCustomRoles: string[];
}

interface FilterCustomerRequest {
  assignedTo: number | null;
  customerName: string;
  limit?: number;
}

export interface CustomerConcept {
  name: string;
  id: number;
}

export enum CustomerType {
  Brand = 0,
  Vendor = 1,
}

interface UserEntity {
  id: number;
}

interface CustomerBranchData {
  customerId: number;
  branchId: number;
  deliveryTermId: number;
  paymentTermId: number;
  useDefaultCurrency: boolean;
  defaultCurrencyIsMain: boolean;
  currencies: CustomerBranchCurrency;
}

interface CustomerBranchCurrency {
  currencyId: number;
  currencyCode: string;
  isMain: boolean;
  useCustomSellingRate: boolean;
  sellingRate: number;
  garpSellingRate: number;
}

export interface CreateCustomerRequest {
  name: string;
  customerType: number;
  bcSampleApprovalRequired: boolean;
}

export interface UpdateCustomerRequest {
  name: string;
  phone: string;
  email: string;
  officeAddress: string;
  website: string;
  notes: string;
  bcSampleApprovalRequired: boolean;
  customerType: CustomerType;
}

export interface Customer {
  id: number;
  name: string;
  identifier: string;
  officeAddress: string;
  website: string;
  email: string;
  phone: string;
  notes: string;
  users: UserEntity[];
  organizationId: number;
  folderId: number;
  logoId?: number;
  archived: boolean;
  customerType: CustomerType;
  bcSampleApprovalRequired: boolean;
  branchData: CustomerBranchData;
}

export interface CustomerListItem {
  id: number;
  name: string;
}

export interface CustomerDirectoryListItem {
  id: number;
  name: string;
  logoIdentifier: string | null;
  archived: string | null;
  customerType: CustomerType;
  erpId: string | null;
  project: string | null;
  articleCount: number;
  currentUserRoles: string[];
  currentUserCustomRoles: string[];
}

export interface CustomerSearchQuery {
  searchTerm: string;
  includeArchived: boolean;
  sortBy: string;
  sortDesc: boolean;
  page: number;
  pageSize: number;
  type?: CustomerType;
  assignedToCurrentUser?: boolean;
  currentUserRole?: CustomerUserRoleFilter;
}

export interface CustomerUserRoleFilter {
  userType: UserType | null;
}

export interface CustomerSlim {
  id: number;
  name: string;
}

export const customersApi = {
  getComplianceDocuments: (customerId: number) =>
    httpClient.get<CustomerComplianceDocument[]>(
      `customers/${customerId}/compliance-documents`,
    ),
  assignComplianceDocument: (
    customerId: number,
    definitionId: number,
    request: CustomerComplianceDocumentBranchesRequest,
  ) =>
    httpClient.put(
      `customers/${customerId}/compliance-documents/${definitionId}`,
      { data: request },
    ),
  updateComplianceDocumentBranches: (
    customerId: number,
    definitionId: number,
    request: CustomerComplianceDocumentBranchesRequest,
  ) =>
    httpClient.put(
      `customers/${customerId}/compliance-documents/${definitionId}/branches`,
      { data: request },
    ),
  removeComplianceDocument: (customerId: number, definitionId: number) =>
    httpClient.delete(
      `customers/${customerId}/compliance-documents/${definitionId}`,
    ),
  get: (customerId: number) =>
    httpClient.get<Customer>(`customers/${customerId}`),
  search: (request: CustomerSearchQuery) =>
    httpClient.post<PagedItems<CustomerDirectoryListItem>>('customers/search', {
      data: request,
    }),
  update: (customerId: number, request: UpdateCustomerRequest) =>
    httpClient.put<Customer>(`customers/${customerId}`, { data: request }),
  archive: (customerId: number) =>
    httpClient.put<Customer>(`customers/${customerId}/archive`),
  restore: (customerId: number) =>
    httpClient.put<Customer>(`customers/${customerId}/restore`),
  filterCustomers: (request: FilterCustomerRequest) =>
    httpClient.post<BrandsDto[]>('customers/filter', { data: request }),
  getConcepts: (customerId: number) =>
    httpClient.get<CustomerConcept[]>(`customers/${customerId}/concepts`, {
      params: { includeArticles: false },
    }),
  createCustomer: (request: CreateCustomerRequest) =>
    httpClient.post<Customer>(`organizations/3/customers`, {
      data: request,
    }),
  getAll: () => httpClient.get<CustomerListItem[]>(`organizations/3/customers`),
  getSlim: (id: number) => httpClient.get<CustomerSlim>(`customers/${id}/slim`),
  updateLogo: (customerId: number, fileId: number) =>
    httpClient.put(`customers/${customerId}/logo/${fileId}`),
  deleteLogo: (customerId: number) =>
    httpClient.delete(`customers/${customerId}/logo`),
};
